// src/context/SearchContext.jsx
import React, { createContext, useContext, useState, useMemo } from 'react';

const SearchContext = createContext();

export function SearchProvider({ children }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [priceRange, setPriceRange] = useState({ min: 0, max: Infinity });
  const [sortBy, setSortBy] = useState('default');
  // Sıralama seçenekleri: default, price-asc, price-desc, rating, newest, popular, bestseller

  // Filtreleme fonksiyonu - ürün listesini filtreler
  const filterProducts = (products) => {
    if (!Array.isArray(products)) return [];
    
    let filtered = [...products];

    // Arama sorgusu filtresi
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.brand?.toLowerCase().includes(query)
      );
    }

    // Kategori filtresi
    if (selectedCategory) {
      filtered = filtered.filter(product =>
        product.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Fiyat aralığı filtresi
    filtered = filtered.filter(product => {
      const price = parseFloat(product.price) || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    // Sıralama
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
        break;
      case 'price-desc':
        filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });
        break;
      case 'popular':
        filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        break;
      case 'bestseller':
        filtered.sort((a, b) => (b.sales || 0) - (a.sales || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setPriceRange({ min: 0, max: Infinity });
    setSortBy('default');
  };

  // Aktif filtre sayısı
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (selectedCategory) count++;
    if (priceRange.min > 0 || priceRange.max < Infinity) count++;
    if (sortBy !== 'default') count++;
    return count;
  }, [searchQuery, selectedCategory, priceRange, sortBy]);

  // Filtre aktif mi kontrol
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <SearchContext.Provider value={{
      // State
      searchQuery,
      setSearchQuery,
      selectedCategory,
      setSelectedCategory,
      priceRange,
      setPriceRange,
      sortBy,
      setSortBy,
      
      // Fonksiyonlar
      filterProducts,
      clearFilters,
      
      // Hesaplamalar
      activeFilterCount,
      hasActiveFilters
    }}>
      {children}
    </SearchContext.Provider>
  );
}

// Custom hook
export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}

export default SearchContext;